/**
 * WebSocket Service để nhận tín hiệu logout từ backend
 * Khi backend nhận được front-channel logout callback từ Microsoft,
 * nó sẽ gửi message qua WebSocket để client tự động logout
 */

class WebSocketService {
  constructor() {
    this.ws = null;
    this.reconnectInterval = null;
    this.reconnectDelay = 60000; // 5 giây
    this.maxReconnectAttempts = 10;
    this.reconnectAttempts = 0;
    this.token = null;
    this.sid = null;
    this.onLogoutCallback = null;
    this.isIntentionallyClosed = false;
  }

  /**
   * Kết nối WebSocket với backend
   * @param {string} token - Access token để xác thực
   * @param {string} sid - Session ID từ Microsoft
   * @param {Function} onLogout - Callback khi nhận được lệnh logout
   */
  connect(token, sid, onLogout) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      // console.log("🔵 WebSocket đã được kết nối");
      return;
    }

    this.token = token;
    this.sid = sid;
    this.onLogoutCallback = onLogout;
    this.isIntentionallyClosed = false;

    // Lấy WebSocket URL từ API domain
    const apiDomain = process.env.REACT_APP_DOMAIN || window.location.origin;
    const wsProtocol = apiDomain.startsWith("https") ? "wss" : "ws";
    const wsHost = apiDomain.replace(/^https?:\/\//, "");

    // Thêm sid vào query params
    const params = new URLSearchParams({
      token: token,
    });
    if (sid) {
      params.append("sid", sid);
    }
    const wsUrl = `${wsProtocol}://${wsHost}/ws?${params.toString()}`;

    // console.log("🟢 Đang kết nối WebSocket:", wsUrl.replace(token, "TOKEN_HIDDEN"));

    try {
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        // console.log("✅ WebSocket connected successfully");
        this.reconnectAttempts = 0; // Reset counter khi kết nối thành công

        // Gửi ping message để keep-alive
        this.startHeartbeat();
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          // console.log("📨 Nhận message từ WebSocket:", data);

          // Xử lý các loại message khác nhau
          switch (data.type) {
            case "logout":
            case "force_logout":
            case "outlook_logout":
              // console.log("🔴 Nhận lệnh logout từ backend");
              if (this.onLogoutCallback) {
                this.onLogoutCallback(data);
              }
              break;

            case "pong":
              // console.log("💓 Heartbeat pong received");
              break;

            default:
            // console.log("📩 Unknown message type:", data.type);
          }
        } catch (error) {
          // console.error("❌ Lỗi parse WebSocket message:", error);
        }
      };

      this.ws.onerror = (error) => {
        // console.error("❌ WebSocket error:", error);
      };

      this.ws.onclose = (event) => {
        // console.log("🔴 WebSocket closed:", event.code, event.reason);
        this.stopHeartbeat();

        // Chỉ reconnect nếu không phải disconnect có chủ đích
        if (!this.isIntentionallyClosed) {
          this.scheduleReconnect();
        }
      };
    } catch (error) {
      // console.error("❌ Lỗi khi tạo WebSocket:", error);
      this.scheduleReconnect();
    }
  }

  /**
   * Gửi heartbeat để keep connection alive
   */
  startHeartbeat() {
    this.stopHeartbeat(); // Clear existing interval

    this.heartbeatInterval = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        try {
          this.ws.send(JSON.stringify({ type: "ping" }));
          // console.log("💓 Heartbeat ping sent");
        } catch (error) {
          // console.error("❌ Lỗi khi gửi heartbeat:", error);
        }
      }
    }, 30000); // Ping mỗi 30 giây
  }

  /**
   * Dừng heartbeat
   */
  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  /**
   * Lên lịch reconnect
   */
  scheduleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      // console.error("❌ Đã vượt quá số lần reconnect tối đa");
      return;
    }

    if (this.reconnectInterval) {
      return; // Đã có schedule rồi
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * this.reconnectAttempts; // Exponential backoff

    // console.log(
    //   `🔄 Sẽ reconnect WebSocket sau ${delay}ms (lần ${this.reconnectAttempts}/${this.maxReconnectAttempts})`
    // );

    this.reconnectInterval = setTimeout(() => {
      this.reconnectInterval = null;
      if (this.token && this.onLogoutCallback) {
        this.connect(this.token, this.sid, this.onLogoutCallback);
      }
    }, delay);
  }

  /**
   * Ngắt kết nối WebSocket
   */
  disconnect() {
    this.isIntentionallyClosed = true;
    this.stopHeartbeat();

    if (this.reconnectInterval) {
      clearTimeout(this.reconnectInterval);
      this.reconnectInterval = null;
    }

    if (this.ws) {
      try {
        this.ws.close(1000, "Client disconnect");
        // console.log("🔴 WebSocket disconnected");
      } catch (error) {
        // console.error("❌ Lỗi khi đóng WebSocket:", error);
      }
      this.ws = null;
    }

    this.token = null;
    this.sid = null;
    this.onLogoutCallback = null;
    this.reconnectAttempts = 0;
  }

  /**
   * Kiểm tra trạng thái kết nối
   */
  isConnected() {
    return this.ws && this.ws.readyState === WebSocket.OPEN;
  }

  /**
   * Gửi message qua WebSocket
   */
  send(data) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(JSON.stringify(data));
        return true;
      } catch (error) {
        // console.error("❌ Lỗi khi gửi message:", error);
        return false;
      }
    } else {
      // console.warn("⚠️ WebSocket chưa kết nối");
      return false;
    }
  }
}

// Export singleton instance
let wsServiceInstance = null;

export const getWebSocketService = () => {
  if (!wsServiceInstance) {
    wsServiceInstance = new WebSocketService();
  }
  return wsServiceInstance;
};

export default WebSocketService;

